import { http } from '@/core/api';
import { executeRequest } from '@/core/either';
import type { ApiResult } from '@/core/either';
import {
  CREDENTIAL_API_PROVISION_ENDPOINT,
  CREDENTIAL_API_TRANSACTIONS_ENDPOINT,
  buildActivateSecretEndpoint,
  buildBlockSecretEndpoint,
  buildCredentialApiClientEndpoint,
  buildCredentialApiSecretIpsEndpoint,
  buildCredentialApiTransactionRequestEndpoint,
} from '@/modules/credentialApi/constants/credentialApi.constant';
import type {
  CreateCredentialApiClientPayload,
  CreateCredentialApiClientResponse,
  CredentialApiClientResponse,
  CredentialApiPurposeResponse,
  CredentialApiSecretActionParams,
  CredentialApiSecretIpsResponse,
  CredentialApiTransactionRequestResponse,
  CredentialApiTransactionsPageResponse,
  CredentialApiTransactionsParams,
  UpdateCredentialApiSecretIpsPayload,
} from '@/modules/credentialApi/types/credentialApi.type';

export const credentialApiService = {
  /**
   * Obtiene el listado de clientes del recurso Credenciales API.
   * El backend resuelve el contexto del cliente a partir de la sesión
   * autenticada, por lo que este método no recibe `clientId` como parámetro.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getClients(): Promise<ApiResult<CredentialApiClientResponse[]>> {
    return executeRequest(() =>
      http.get<CredentialApiClientResponse[]>('business-api/b2b/clients')
    );
  },

  /**
   * Obtiene el catálogo de propósitos que se pueden asignar a una credencial API.
   * @param options - Opciones adicionales de la request.
   * @param options.signal - AbortSignal para cancelar la request en vuelo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getPurposes(options?: {
    signal?: AbortSignal;
  }): Promise<ApiResult<CredentialApiPurposeResponse[]>> {
    return executeRequest(() =>
      http.get<CredentialApiPurposeResponse[]>(
        'business-api/b2b/api-credentials/purposes',
        { signal: options?.signal }
      )
    );
  },

  /**
   * El cuerpo enviado es EXACTAMENTE `{ name, ips, purposes }`. Los valores de
   * autenticación OTP (`authValue`, `authType`) provienen del flujo normal
   * `useOtpFlow` con `business_flow: API_SECRET_REQUESTED` y se envían como
   * cabeceras HTTP, replicando el patrón de `saveContactBank` en contactos.
   */
  createClient({
    payload,
    authValue,
    authType,
  }: CreateCredentialApiClientPayload): Promise<
    ApiResult<CreateCredentialApiClientResponse>
  > {
    return executeRequest(() =>
      http.post<CreateCredentialApiClientResponse>(
        CREDENTIAL_API_PROVISION_ENDPOINT,
        payload,
        { headers: { authValue, authType } }
      )
    );
  },

  getClientSecretIps(
    params: { clientId: string; secretId: string }
  ): Promise<ApiResult<CredentialApiSecretIpsResponse>> {
    return executeRequest(() =>
      http.get<CredentialApiSecretIpsResponse>(
        buildCredentialApiSecretIpsEndpoint(params)
      )
    );
  },

  /**
   * El cuerpo enviado es EXACTAMENTE `{ ips: string[] }`. La operación
   * NO requiere OTP y, por lo tanto, no se envían cabeceras `authValue` /
   * `authType` (a diferencia de `createClient`).
   */
  updateClientSecretIps(
    params: UpdateCredentialApiSecretIpsPayload
  ): Promise<ApiResult<CredentialApiSecretIpsResponse>> {
    return executeRequest(() =>
      http.put<CredentialApiSecretIpsResponse>(
        buildCredentialApiSecretIpsEndpoint(params),
        params.payload
      )
    );
  },

  /**
   * La operación NO requiere OTP ni PIN y, por lo tanto, no se envían
   * cabeceras de autenticación. El backend responde 204 No Content en
   * caso de éxito, por lo que el tipo de retorno se modela como
   * `ApiResult<void>`.
   */
  deleteClient(clientId: string): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.delete<void>(buildCredentialApiClientEndpoint(clientId))
    );
  },

  /**
   * Bloquea el secret activo de una credencial API. Endpoint:
   *   `POST /business-api/b2b/clients/{clientId}/secrets/{secretId}/block`
   *
   * La operación NO requiere OTP ni PIN y, por lo tanto, no se envía
   * cuerpo ni cabeceras de autenticación. El backend responde 200 OK
   * sin cuerpo en caso de éxito, por lo que el tipo de retorno se
   * modela como `ApiResult<void>`.
   * Source: SDD change `credential-api-status-actions`.
   *
   * @param params.clientId - Identificador del cliente.
   * @param params.secretId - Identificador del secret activo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  blockClient(
    params: CredentialApiSecretActionParams
  ): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>(buildBlockSecretEndpoint(params))
    );
  },

  /**
   * Activa el secret bloqueado de una credencial API. Endpoint:
   *   `POST /business-api/b2b/clients/{clientId}/secrets/{secretId}/activate`
   *
   * La operación NO requiere OTP ni PIN y, por lo tanto, no se envía
   * cuerpo ni cabeceras de autenticación. El backend responde 200 OK
   * sin cuerpo en caso de éxito, por lo que el tipo de retorno se
   * modela como `ApiResult<void>`.
   * Source: SDD change `credential-api-status-actions`.
   *
   * @param params.clientId - Identificador del cliente.
   * @param params.secretId - Identificador del secret activo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  activateClient(
    params: CredentialApiSecretActionParams
  ): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>(buildActivateSecretEndpoint(params))
    );
  },

  /**
   * Obtiene la página de movimientos (transacciones) reportados por
   * la API Transaccional del cliente autenticado.
   * Endpoint:
   *   `GET /business-api/b2b/transactions`
   *
   * El primer fetch se dispara SOLO cuando el usuario activa el tab
   * "Movimientos API" — el composable de movimientos mantiene
   * `usePagination` con `autoLoad: false` hasta entonces.
   *
   * Los filtros `clientIds`, `operationTypes` y `statuses` se envían
   * como cadenas separadas por comas (e.g. `clientIds=cid-a,cid-b`),
   * y `dateFrom` / `dateTo` como `yyyy-MM-dd` (formato de salida
   * del `GDatePicker` configurado con `value-format="YYYY-MM-DD"`).
   * Source: SDD change `credential-api-movements`.
   *
   * @param params - Parámetros de búsqueda paginada y filtros.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getTransactions(
    params: CredentialApiTransactionsParams
  ): Promise<ApiResult<CredentialApiTransactionsPageResponse>> {
    const queryParams: Record<string, string | number> = {
      page: params.page,
      size: params.size,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
    };

    if (params.search !== undefined) {
      queryParams.search = params.search;
    }
    if (params.clientIds !== undefined) {
      queryParams.clientIds = params.clientIds;
    }
    if (params.operationTypes !== undefined) {
      queryParams.operationTypes = params.operationTypes;
    }
    if (params.statuses !== undefined) {
      queryParams.statuses = params.statuses;
    }
    if (params.dateFrom !== undefined) {
      queryParams.dateFrom = params.dateFrom;
    }
    if (params.dateTo !== undefined) {
      queryParams.dateTo = params.dateTo;
    }

    return executeRequest(() =>
      http.get<CredentialApiTransactionsPageResponse>(
        CREDENTIAL_API_TRANSACTIONS_ENDPOINT,
        { params: queryParams }
      )
    );
  },

  /**
   * Obtiene el payload crudo del request asociado a una transacción
   * específica de la API Transaccional. Endpoint:
   *   `GET /business-api/b2b/transactions/{transactionId}/request`
   *
   * El cuerpo retornado es JSON arbitrario y se renderiza formateado en
   * el `TransactionRequestDialog`. Se modela como
   * `CredentialApiTransactionRequestResponse` (`JsonValue`) y NO se
   * declara un tipo más estricto para evitar acoplarse al shape del
   * payload de un proveedor externo.
   * Source: SDD change `credential-api-movement-detail-request`.
   *
   * @param transactionId - Identificador canónico de la transacción.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getTransactionRequest(
    transactionId: string | number
  ): Promise<ApiResult<CredentialApiTransactionRequestResponse>> {
    return executeRequest(() =>
      http.get<CredentialApiTransactionRequestResponse>(
        buildCredentialApiTransactionRequestEndpoint(transactionId)
      )
    );
  },
};
